import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { edgeHivas } from '../lib/edge.ts';
import { supabase } from '../lib/supabase.ts';
import { SZEREP_CIMKE, TIPUS_CIMKE, type IngatlanTipus, type TagSzerep } from '../lib/tipusok.ts';
import { Gomb, HibaUzenet, Parbeszed, Valaszto } from '../ui/elemek.tsx';
import MeghivoBlokk from './MeghivoBlokk.tsx';
import { nemKeszJelzes, type Meghivo } from './meghivoAllapot.ts';

export interface Tag {
  user_id: string; szerep: TagSzerep; aktiv: boolean; szabadsagon: boolean;
  tipus_szuro: IngatlanTipus[] | null; utolso_kiosztas_at: string | null;
  profil: { nev: string; email: string; telefon: string | null } | null;
}
type Modositas = Partial<Pick<Tag, 'szerep' | 'aktiv' | 'szabadsagon' | 'tipus_szuro'>>;

const TIPUSOK = Object.keys(TIPUS_CIMKE) as IngatlanTipus[];
const NYITOTT_STATUSZOK = ['uj', 'elerve', 'szemle_egyeztetve', 'szemle_megtortent', 'megbizas'] as const;

function Kapcsolo({ label, bekapcsolva, onValtozas, disabled }: { label: string; bekapcsolva: boolean; onValtozas(v: boolean): void; disabled?: boolean }) {
  return (
    <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm">
      <input type="checkbox" role="switch" checked={bekapcsolva} disabled={disabled}
        onChange={(e) => onValtozas(e.target.checked)} className="size-5 accent-kiemelt" />
      <span>{label}</span>
    </label>
  );
}

export default function TagSor({ tag, officeId, sajat, meghivo, kesz }: { tag: Tag; officeId: string; sajat: boolean; meghivo: Meghivo | null; kesz?: boolean }) {
  const qc = useQueryClient();
  const [inaktivalas, setInaktivalas] = useState(false);
  const [mfaKerdes, setMfaKerdes] = useState(false);
  const [mfaKesz, setMfaKesz] = useState(false);
  const nev = tag.profil?.nev ?? 'Névtelen';
  const nemKesz = nemKeszJelzes(tag.aktiv, meghivo, kesz);

  const nyitottLeadek = useQuery({
    queryKey: ['tag-nyitott-leadek', officeId, tag.user_id],
    enabled: inaktivalas,
    gcTime: 0,
    queryFn: async () => {
      const { count, error } = await supabase.from('leads').select('id', { count: 'exact', head: true })
        .eq('office_id', officeId).eq('assigned_to', tag.user_id).in('statusz', [...NYITOTT_STATUSZOK]);
      if (error) throw error;
      return count ?? 0;
    },
  });

  const modosit = useMutation({
    mutationFn: async (valtozas: Modositas) => {
      const { error } = await supabase.from('office_members').update(valtozas)
        .eq('office_id', officeId).eq('user_id', tag.user_id);
      if (error) throw error;
    },
    // Siker és hiba után is újratöltjük a listát, így hibánál a sor visszaáll a tárolt állapotra.
    onSettled: () => qc.invalidateQueries({ queryKey: ['kollegak', officeId] }),
  });
  const mfaVisszaallitas = useMutation({
    mutationFn: () => edgeHivas('admin-mfa-visszaallitas', { userId: tag.user_id }),
    onSuccess: () => { setMfaKerdes(false); setMfaKesz(true); },
  });

  const szuro = tag.tipus_szuro ?? [];
  function tipusValtas(tipus: IngatlanTipus, be: boolean) {
    const uj = be ? [...szuro, tipus] : szuro.filter((t) => t !== tipus);
    // Mind vagy egyik sem = minden típus (null).
    modosit.mutate({ tipus_szuro: uj.length === 0 || uj.length === TIPUSOK.length ? null : uj });
  }

  return (
    <section className={`px-5 py-5 ${tag.aktiv ? '' : 'opacity-70'}`}>
      <div className="grid gap-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-base font-medium tracking-[-0.01em]">{nev}{sajat && <span className="ml-2 text-sm font-normal text-szoveg-halk">(te)</span>}</h3>
            <p className="break-words text-sm text-szoveg-halk">{tag.profil?.email}</p>
            {tag.profil?.telefon && <p className="text-sm text-szoveg-halk">{tag.profil.telefon}</p>}
          </div>
          <Valaszto label="Szerep" value={tag.szerep} disabled={modosit.isPending}
            onChange={(e) => modosit.mutate({ szerep: e.target.value as TagSzerep })} className="w-44">
            <option value="ertekesito">{SZEREP_CIMKE.ertekesito}</option>
            <option value="vezeto">{SZEREP_CIMKE.vezeto}</option>
          </Valaszto>
        </div>

        {meghivo && tag.profil && (
          <MeghivoBlokk meghivo={meghivo} officeId={officeId} email={tag.profil.email} nev={tag.profil.nev || nev}
            szerep={tag.szerep} aktiv={tag.aktiv} />
        )}

        {nemKesz && <p role="status" className="rounded-xl border border-vonal bg-felulet px-3.5 py-3 text-sm text-szoveg-halk">{nemKesz}</p>}

        <div className="flex flex-wrap gap-x-6">
          <Kapcsolo label="Aktív" bekapcsolva={tag.aktiv} disabled={modosit.isPending}
            onValtozas={(be) => (be ? modosit.mutate({ aktiv: true }) : setInaktivalas(true))} />
          <Kapcsolo label="Szabadságon" bekapcsolva={tag.szabadsagon} disabled={modosit.isPending}
            onValtozas={(be) => modosit.mutate({ szabadsagon: be })} />
        </div>

        <fieldset className="grid gap-1">
          <legend className="text-sm text-szoveg-halk">Milyen típusú leadet kaphat?</legend>
          <div className="flex flex-wrap gap-x-4">
            {TIPUSOK.map((t) => (
              <label key={t} className="flex min-h-11 cursor-pointer items-center gap-2 text-sm">
                <input type="checkbox" checked={szuro.includes(t)} disabled={modosit.isPending}
                  onChange={(e) => tipusValtas(t, e.target.checked)} className="size-5 accent-kiemelt" />
                {TIPUS_CIMKE[t]}
              </label>
            ))}
          </div>
          <p className="text-sm text-szoveg-halk">Ha egyiket sem jelölöd, minden típusú leadet kaphat.</p>
        </fieldset>

        <HibaUzenet hiba={modosit.error} />
        {mfaKesz && <p role="status" className="rounded-xl border border-siker px-3.5 py-2.5 text-sm text-siker">Visszaállítva. A következő belépéskor új alkalmazást kell beállítania.</p>}

        {!sajat && (
          <div>
            <Gomb valtozat="szoveg" className="-ml-4" onClick={() => { setMfaKesz(false); mfaVisszaallitas.reset(); setMfaKerdes(true); }}>
              Kétlépcsős azonosítás visszaállítása
            </Gomb>
          </div>
        )}
      </div>

      <Parbeszed nyitva={inaktivalas} cim={`${nev} inaktiválása`} onBezar={() => setInaktivalas(false)}>
        {nyitottLeadek.isPending && <p className="text-szoveg-halk">Nyitott leadek ellenőrzése…</p>}
        <HibaUzenet hiba={nyitottLeadek.error} />
        {nyitottLeadek.data !== undefined && (
          <p>
            {nyitottLeadek.data > 0
              ? `${nyitottLeadek.data} nyitott leadje van. Inaktiválás után ezeket csak te látod; add át őket másnak.`
              : 'Nincs nyitott leadje. Inaktiválás után nem lép be az irodába, és nem kap új leadet.'}
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <Gomb valtozat="szoveg" onClick={() => setInaktivalas(false)}>Mégse</Gomb>
          <Gomb valtozat="veszely" toltes={modosit.isPending} disabled={nyitottLeadek.isPending}
            onClick={() => modosit.mutate({ aktiv: false }, { onSuccess: () => setInaktivalas(false), onError: () => setInaktivalas(false) })}>
            Inaktiválás
          </Gomb>
        </div>
      </Parbeszed>

      <Parbeszed nyitva={mfaKerdes} cim="Kétlépcsős azonosítás visszaállítása" onBezar={() => setMfaKerdes(false)}>
        <p>{nev} hitelesítő alkalmazását töröljük, és e-mailben értesítjük. A következő belépéskor újat kell beállítania.</p>
        <HibaUzenet hiba={mfaVisszaallitas.error} />
        <div className="flex flex-wrap justify-end gap-2">
          <Gomb valtozat="szoveg" onClick={() => setMfaKerdes(false)}>Mégse</Gomb>
          <Gomb valtozat="veszely" toltes={mfaVisszaallitas.isPending} onClick={() => mfaVisszaallitas.mutate()}>Visszaállítás</Gomb>
        </div>
      </Parbeszed>
    </section>
  );
}
